const CONTACT_DRAFT_CONTINUITY_KEY = "smartmanager:landing-contact-locale-draft";
const CONTACT_DRAFT_WINDOW_KEY = "__smartManagerLandingContactLocaleDraft";

type ContactDraftWindow = Window & {
  __smartManagerLandingContactLocaleDraft?: LandingContactDraftContinuity;
};

export const LANDING_CONTACT_LOCALE_SNAPSHOT_EVENT =
  "smartmanager:landing-contact-locale-snapshot";

export type LandingContactDraftContinuity = {
  email: string;
  message: string;
  subject: string;
  usesDefaultSubject: boolean;
  deliveryState: "draft" | "sent" | "unknown";
};

let inMemoryDraft: LandingContactDraftContinuity | null = null;

function normalizeBoundedText(value: unknown, maxLength: number): string | null {
  return typeof value === "string" && value.length <= maxLength ? value : null;
}

function normalizeContactDraft(value: unknown): LandingContactDraftContinuity | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  const email = normalizeBoundedText(candidate.email, 254);
  const subject = normalizeBoundedText(candidate.subject, 160);
  const message = normalizeBoundedText(candidate.message, 10_000);
  const deliveryState = candidate.deliveryState === "draft"
    || candidate.deliveryState === "sent"
    || candidate.deliveryState === "unknown"
    ? candidate.deliveryState
    : null;
  if (
    email === null
    || subject === null
    || message === null
    || typeof candidate.usesDefaultSubject !== "boolean"
    || deliveryState === null
  ) return null;
  return {
    email,
    subject,
    message,
    usesDefaultSubject: candidate.usesDefaultSubject,
    deliveryState,
  };
}

/** Captures the draft only for the imminent locale remount, then consumes it once. */
export function persistLandingContactDraftContinuity(value: LandingContactDraftContinuity): void {
  if (typeof window === "undefined") return;
  const normalized = normalizeContactDraft(value);
  if (!normalized) return;
  inMemoryDraft = normalized;
  (window as ContactDraftWindow)[CONTACT_DRAFT_WINDOW_KEY] = normalized;
  try {
    window.sessionStorage.setItem(CONTACT_DRAFT_CONTINUITY_KEY, JSON.stringify(normalized));
  } catch {
    // The in-memory copy still protects same-document locale remounts.
  }
}

export function consumeLandingContactDraftContinuity(): LandingContactDraftContinuity | null {
  if (typeof window === "undefined") return null;
  const continuityWindow = window as ContactDraftWindow;
  const memoryValue = continuityWindow[CONTACT_DRAFT_WINDOW_KEY] ?? inMemoryDraft;
  try {
    const raw = window.sessionStorage.getItem(CONTACT_DRAFT_CONTINUITY_KEY);
    return normalizeContactDraft(raw ? JSON.parse(raw) : memoryValue) ?? memoryValue;
  } catch {
    return memoryValue;
  }
}

/** Clears the handoff only after the restored Contact form has committed. */
export function clearLandingContactDraftContinuity(): void {
  if (typeof window === "undefined") return;
  const continuityWindow = window as ContactDraftWindow;
  delete continuityWindow[CONTACT_DRAFT_WINDOW_KEY];
  inMemoryDraft = null;
  try {
    window.sessionStorage.removeItem(CONTACT_DRAFT_CONTINUITY_KEY);
  } catch {
    // The same-document copies are already cleared.
  }
}

export function requestLandingContactDraftContinuity(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(LANDING_CONTACT_LOCALE_SNAPSHOT_EVENT));

  // React may be between effect cleanup and re-registration when the locale
  // selector changes immediately after an editor input. The rendered controls
  // are the authoritative user-visible draft at this boundary.
  const emailInput = window.document.querySelector<HTMLInputElement>(
    "[data-landing-contact-email]",
  );
  const subjectInput = window.document.querySelector<HTMLInputElement>(
    "[data-landing-contact-subject-input]",
  );
  const subjectContainer = window.document.querySelector<HTMLElement>(
    "[data-landing-contact-subject]",
  );
  const messageInput = window.document.querySelector<HTMLTextAreaElement>(
    "[data-landing-contact-message-input]",
  );
  const messageContainer = window.document.querySelector<HTMLElement>(
    "[data-landing-contact-message]",
  );
  if (!emailInput || !subjectContainer || !messageContainer) return;
  const renderedDeliveryState = subjectContainer.dataset.landingContactDeliveryState;
  const baseDraft: LandingContactDraftContinuity = inMemoryDraft ?? {
    email: emailInput.value,
    subject: subjectContainer.dataset.landingContactSubject ?? "",
    message: messageContainer.dataset.landingContactMessage ?? "",
    usesDefaultSubject: subjectContainer.dataset.landingContactUsesDefaultSubject === "true",
    deliveryState: renderedDeliveryState === "sent" || renderedDeliveryState === "unknown"
      ? renderedDeliveryState
      : "draft",
  };
  persistLandingContactDraftContinuity({
    ...baseDraft,
    email: emailInput.value,
    subject: subjectInput?.value
      ?? subjectContainer?.dataset.landingContactSubject
      ?? baseDraft.subject,
    message: messageInput?.value
      ?? messageContainer?.dataset.landingContactMessage
      ?? baseDraft.message,
  });
}

export const __test__ = { normalizeContactDraft };
